import { Suspense, useEffect, useMemo, useRef, useState } from 'react';
import { lazyScreen } from '../../app/lazyScreen';
import { LayoutList, Plus, Search } from 'lucide-react';
import { Link, useLocation } from 'react-router';
import { useTranslation } from 'react-i18next';
import { useServices } from '../../app/services';
import { useToday } from '../../app/today';
import { AppHeader, Button, ButtonLink, EmptyState, MonthSwitcher, addMonths, type YearMonth } from '../../ui';
// The sample preview draws charts; only people who turn it on load them (Phase 26).
const HomeDashboard = lazyScreen(
  () => import('./HomeDashboard'),
  (m) => m.HomeDashboard,
);
import { mockDashboard } from './mockDashboard';
import { HomeLoading, RecentHome, useMonthEntries } from './RecentHome';
import { PaceSection } from './PaceSection';
import { AnomalyList } from './Anomalies';
import { useMonthInsights } from './insights';
import { useHomeLayout } from './layout';
import { CustomizeHome } from './CustomizeHome';
import { HomeTrend } from './HomeTrend';
import { DueBills } from '../recurring/RecurringScreen';
import { BudgetPill } from '../spaces/BudgetSwitcher';
import { ReadOnlyNotice, SyncPausedNotice } from '../../premium/ReadOnlyNotice';
import { ConflictNotice } from '../activity/ConflictNotice';

const monthOfDay = (day: string): YearMonth => ({ year: Number(day.slice(0, 4)), month: Number(day.slice(5, 7)) });
const same = (a: YearMonth, b: YearMonth) => a.year === b.year && a.month === b.month;

/**
 * Home. Real numbers arrive with the analytics phase; until then the
 * dashboard is only shown with clearly labelled sample data (More → Preview
 * with sample data) and a real budget shows its honest empty state.
 */
export function HomeScreen() {
  const { t } = useTranslation();
  const { language, activeSpace, sampleData } = useServices();
  const location = useLocation();
  const today = useToday();
  const latest = useMemo(() => monthOfDay(today), [today]);
  const [month, setMonth] = useState(latest);
  // A new month while the app is open: someone looking at "this month" moves on with it.
  const shownLatest = useRef(latest);
  useEffect(() => {
    const before = shownLatest.current;
    if (!same(latest, before)) {
      setMonth((m) => (same(m, before) ? latest : m));
      shownLatest.current = latest;
    }
  }, [latest]);
  const model = useMemo(() => (sampleData ? mockDashboard(month, activeSpace?.currency) : null), [sampleData, month, activeSpace]);
  const real = useMonthEntries(month);
  const isLatest = same(month, latest);
  const bills = isLatest ? <DueBills /> : null;
  // V1.5: where the month in progress is heading, and what is out of the ordinary in the month shown.
  const monthKey = `${month.year}-${String(month.month).padStart(2, '0')}`;
  const insights = useMonthInsights(monthKey, today);
  const home = useHomeLayout();
  const [customizing, setCustomizing] = useState(false);
  const slots =
    insights && real
      ? {
          pace: insights.pace && <PaceSection pace={insights.pace} />,
          anomalies: (
            <AnomalyList
              anomalies={insights.anomalies.filter((a) => !insights.dismissed.has(a.key))}
              month={monthKey}
              current={isLatest}
              categories={real.categories}
              subcategories={real.subcategories}
              title={t('anomaly.title')}
              limit={3}
              onDismiss={insights.dismiss}
            />
          ),
          bills,
          trend: <HomeTrend trend={insights.trend} onSelectMonth={(m) => setMonth({ year: Number(m.slice(0, 4)), month: Number(m.slice(5, 7)) })} />,
        }
      : { bills };

  return (
    <>
      <AppHeader
        actions={
          <span className="header-pills">
            {sampleData && (
              <span className="pill pill-sample" data-testid="sample-badge">
                {t('home.sample')}
              </span>
            )}
            {!sampleData && (
              <Link
                to="/entries"
                state={{ focusSearch: true }}
                className="icon-btn icon-btn-ghost"
                aria-label={t('entries.open')}
                aria-keyshortcuts="/"
                data-testid="open-entries"
              >
                <Search size={20} aria-hidden />
              </Link>
            )}
            <BudgetPill />
          </span>
        }
      />
      {!sampleData && <ConflictNotice />}
      {!sampleData && activeSpace && <ReadOnlyNotice space={activeSpace} />}
      {!sampleData && <SyncPausedNotice />}
      <MonthSwitcher value={month} onChange={setMonth} language={language} max={latest} min={sampleData ? addMonths(latest, -23) : undefined} />
      {model ? (
        <Suspense fallback={<HomeLoading />}>
          <HomeDashboard
            model={model}
            language={language}
            onSelectMonth={(key) => {
              const [y, m] = key.split('-').map(Number);
              setMonth({ year: y!, month: m! });
            }}
          />
        </Suspense>
      ) : real === null ? (
        <HomeLoading />
      ) : real.count > 0 ? (
        <>
          <RecentHome data={real} layout={home.layout} slots={slots} />
          <div className="home-customize">
            <Button variant="ghost" icon={LayoutList} onClick={() => setCustomizing(true)} aria-haspopup="dialog">
              {t('customize.open')}
            </Button>
          </div>
          <CustomizeHome open={customizing} onClose={() => setCustomizing(false)} layout={home.layout} onChange={home.change} />
        </>
      ) : (
        <>
          {bills}
          <EmptyState
            title={t('home.emptyTitle')}
            body={t('home.emptyBody')}
            action={
              <ButtonLink to="/add" state={{ background: location }} variant="primary" size="lg" icon={Plus}>
                {t('home.addFirst')}
              </ButtonLink>
            }
          />
        </>
      )}
    </>
  );
}
